// src/admin/ProductManagement.js
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, doc, setDoc, updateDoc, deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { uploadToImageKit } from '../utils/imagekitUpload';
import {
  Plus, Edit, Trash2, X, Upload, Package, Search,
  Star, Image as ImageIcon
} from 'lucide-react';

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');

  const defaultFormData = {
    title: '',
    category: '',
    price: '',
    originalPrice: '',
    inventory: '',
    description: '',
    images: [], // Multiple images array
    rating: 5,
    reviews: 0,
    featured: false,
  };

  const [formData, setFormData] = useState(defaultFormData);

  // ================= FETCH PRODUCTS & CATEGORIES =================
  const fetchData = async () => {
    setLoading(true);
    try {
      const [prodSnap, catSnap] = await Promise.all([
        getDocs(collection(db, 'products')),
        getDocs(collection(db, 'categories')),
      ]);
      const prodData = prodSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      prodData.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setProducts(prodData);

      const catData = catSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCategories(catData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ================= MULTIPLE IMAGE UPLOAD =================
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const uploadPromises = files.map(file => uploadToImageKit(file, 'products'));
      const urls = await Promise.all(uploadPromises);

      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...urls],
      }));

      setMessage({ type: 'success', text: `${urls.length} image(s) uploaded!` });
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: 'Image upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  // ================= REMOVE IMAGE =================
  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  // ================= SAVE PRODUCT =================
  const handleSave = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validation
    if (!formData.title || !formData.category || !formData.price || formData.images.length === 0) {
      setMessage({ type: 'error', text: 'Title, Category, Price, aur at least 1 Image zaroori hai.' });
      return;
    }

    const productData = {
      title: formData.title,
      category: formData.category,
      price: Number(formData.price),
      originalPrice: formData.originalPrice ? Number(formData.originalPrice) : 0,
      inventory: formData.inventory ? Number(formData.inventory) : 0,
      description: formData.description || '',
      images: formData.images,
      image: formData.images[0] || '', // First image as main
      rating: Number(formData.rating) || 5,
      reviews: Number(formData.reviews) || 0,
      featured: formData.featured === true,
    };

    try {
      if (editingProduct) {
        await updateDoc(doc(db, 'products', editingProduct.id), productData);
        setMessage({ type: 'success', text: 'Product updated!' });
      } else {
        const newId = Date.now().toString();
        await setDoc(doc(db, 'products', newId), {
          ...productData,
          createdAt: new Date(),
        });
        setMessage({ type: 'success', text: 'Product created!' });
      }

      setTimeout(() => {
        setShowModal(false);
        setEditingProduct(null);
        setFormData(defaultFormData);
        fetchData();
      }, 1000);

    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save product.' });
    }
  };

  // ================= DELETE =================
  const handleDelete = async (product) => {
    if (!window.confirm(`Delete product "${product.title}"?`)) return;
    try {
      await deleteDoc(doc(db, 'products', product.id));
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete product.');
    }
  };

  // ================= MODAL =================
  const openAddModal = () => {
    setEditingProduct(null);
    setFormData(defaultFormData);
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title || '',
      category: product.category || '',
      price: product.price || '',
      originalPrice: product.originalPrice || '',
      inventory: product.inventory || '',
      description: product.description || '',
      images: product.images && product.images.length > 0
        ? product.images
        : (product.image ? [product.image] : []),
      rating: product.rating || 5,
      reviews: product.reviews || 0,
      featured: product.featured === true,
    });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  // ================= FILTER =================
  const filteredProducts = products.filter(p =>
    p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Products Management</h1>
          <p className="text-gray-500 text-sm mt-1">Products add, edit, aur delete karein.</p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-green-700 text-white px-5 py-3 rounded-lg hover:bg-green-800 transition flex items-center gap-2 font-semibold shadow-md"
        >
          <Plus size={18} /> Add Product
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-lg py-3 px-4 pl-11 focus:outline-none focus:border-green-600 text-sm"
          />
          <Search className="absolute left-3 top-3.5 text-gray-400" size={18} />
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="text-center py-16">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
          <p className="mt-4 text-gray-500">Loading products...</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Package size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No products found. Add your first product!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredProducts.map((product) => (
            <div key={product.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition">
              <div className="relative h-40 bg-gray-100">
                <img
                  src={product.image || product.images?.[0] || 'https://via.placeholder.com/300'}
                  alt={product.title}
                  className="w-full h-full object-cover"
                />
                {product.images && product.images.length > 1 && (
                  <span className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full">
                    +{product.images.length - 1} more
                  </span>
                )}
                {product.featured && (
                  <span className="absolute top-2 left-2 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                    <Star size={10} className="fill-white" /> Featured
                  </span>
                )}
              </div>
              <div className="p-4">
                <p className="text-xs text-green-700 font-semibold uppercase mb-1">{product.category}</p>
                <h3 className="font-bold text-gray-800 line-clamp-1">{product.title}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-lg font-bold text-green-800">Rs. {product.price}</span>
                  {product.originalPrice > product.price && (
                    <span className="text-xs text-gray-400 line-through">Rs. {product.originalPrice}</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Stock: {product.inventory || 0}</p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => openEditModal(product)}
                    className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-1"
                  >
                    <Edit size={14} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(product)}
                    className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 py-2 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-1"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ================= MODAL ================= */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">

            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-gray-800">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-5 space-y-4">

              {message.text && (
                <div className={`text-sm p-3 rounded-lg ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {message.text}
                </div>
              )}

              {/* ================= MULTIPLE IMAGES ================= */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Product Images * (Multiple allowed)
                </label>

                {/* Image Grid */}
                {formData.images.length > 0 && (
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-3">
                    {formData.images.map((url, i) => (
                      <div key={i} className="relative group">
                        <img src={url} alt={`Img ${i + 1}`} className="w-full h-24 object-cover rounded-lg" />
                        {i === 0 && (
                          <span className="absolute top-1 left-1 bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                            MAIN
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                <label className="flex flex-col items-center justify-center w-full py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition">
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-green-700 border-t-transparent"></div>
                      <p className="mt-2 text-sm text-gray-600">Uploading...</p>
                    </>
                  ) : (
                    <>
                      <Upload className="text-gray-400 mb-2" size={28} />
                      <p className="text-sm font-semibold text-gray-600">Click to upload images</p>
                      <p className="text-xs text-gray-400 mt-1">Multiple select kar sakte hain • PNG, JPG (Max 5 MB each)</p>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              </div>

              {/* ================= TITLE ================= */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Product Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="e.g., Fresh Organic Apples"
                  required
                />
              </div>

              {/* ================= CATEGORY ================= */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm bg-white"
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
                {categories.length === 0 && (
                  <p className="text-xs text-yellow-600 mt-1">
                    ⚠️ Pehle Categories mein jaakar koi category add karein.
                  </p>
                )}
              </div>

              {/* ================= PRICE + DISCOUNT ================= */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Sale Price (Rs.) *</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="450"
                    min="0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Original Price (Rs.)</label>
                  <input
                    type="number"
                    value={formData.originalPrice}
                    onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="600"
                    min="0"
                  />
                  {formData.originalPrice && formData.price && Number(formData.originalPrice) > Number(formData.price) && (
                    <p className="text-xs text-green-600 mt-1">
                      💰 Save Rs. {Number(formData.originalPrice) - Number(formData.price)} ({Math.round(((Number(formData.originalPrice) - Number(formData.price)) / Number(formData.originalPrice)) * 100)}% off)
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Inventory (Stock)</label>
                  <input
                    type="number"
                    value={formData.inventory}
                    onChange={(e) => setFormData({ ...formData, inventory: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="50"
                    min="0"
                  />
                </div>
              </div>

              {/* ================= DESCRIPTION ================= */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none"
                  placeholder="Product ki tafseel likhein..."
                />
              </div>

              {/* ================= RATING + REVIEWS ================= */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Rating (0-5)</label>
                  <input
                    type="number"
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    min="0"
                    max="5"
                    step="0.1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Reviews Count</label>
                  <input
                    type="number"
                    value={formData.reviews}
                    onChange={(e) => setFormData({ ...formData, reviews: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    min="0"
                  />
                </div>
              </div>

              {/* ================= FEATURED ================= */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="w-5 h-5 text-green-700"
                  />
                  <span className="text-sm font-semibold text-gray-700">
                    ⭐ Featured Product (Home page par dikhega)
                  </span>
                </label>
              </div>

              {/* ================= SUBMIT ================= */}
              <div className="flex gap-3 pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className={`flex-1 bg-green-700 text-white py-3 rounded-lg font-semibold hover:bg-green-800 transition flex items-center justify-center gap-2 ${
                    uploading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  <Package size={18} />
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProductManagement;